/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { login } from "./services/CustomerAuth"; //import the login post
import { useNavigate } from "react-router-dom";// Common
import FadeIn from "../common/FadeIn";
import StatusBlock from "../common/StatusBlock";
//Bootstrap
import { Form, Col, Row, Container, Modal, Button } from "react-bootstrap";
//T&C Forms
import TermsAndConditions from "../common/termsAndConditions";
import PrivacyPolicy from "../common/privacyPolicy";
// PageParts 
import AddParticipant from './page-parts/AddParticipant';
//Analytics
import TagManager from "react-gtm-module";
//Context
import { BusinessContextConsumer } from "../BusinessContext";
//Password Warnings
import {
  PasswordStrongSpan,
  PasswordWeakSpan,
  PasswordMediumSpan,
  PasswordMismatchSpan,
} from "../common/PasswordStrengthSpans";
//Images and Icons
import { MdAccountBox, MdSave } from "react-icons/md";

const CustomerSignupForm = (props) => {
  const [customer, setCustomer] = useState({});
  // ViewMode
  const [viewMode, setViewMode] = useState("create"); // create, details, addParticipant
  const [updateKey, setUpdateKey] = useState(0);
  // Handle Context
  const BusinessContext = useContext(BusinessContextConsumer);
  const businessID = BusinessContext.businessID;
  // Error and Click Handling
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitClicked, setSubmitClicked] = useState(false);
  // Other
  const navigate = useNavigate();
  const localAuthCheck =
    localStorage.getItem("authorization-state-customer") &&
    localStorage.getItem("authorization-expire-customer") > Date.now(); // Needed because function won't pass.
  // handle loading states and rerun every time viewMode changes
  const prevUpdateKey = useRef(); // Ref to update
  useEffect(async () => {
    prevUpdateKey.current = updateKey; // Save it for later reloads
    // Handle view modes and get requests
    if (localAuthCheck) {
      const customerDataRequest = await axios.get(`/api/customer/customer_general/customer_view`, {
        withCredentials: true,
      });
      setCustomer(customerDataRequest.data);
      // handle viewmode from state.
      const participants = customerDataRequest.data.Participants;
      let customerID;
      if (customerDataRequest.data.stripe_customer_id) {
        customerID = customerDataRequest.data.stripe_customer_id;
      } else {
        customerID = undefined;
      }

      if (!customerID && participants.length === 0) {
        return setViewMode("details");
      }

      if (customerID && participants.length === 0) {
        return setViewMode("addParticipant");
      }

      if (customerID && participants.length > 0) {
        return navigate("/dashboard/", { replace: true });
      }
    }
  }, [updateKey]);

  // Dismiss Message
  const dismissMessages = () => {
    setSuccessMessage("");
    return setErrorMessage("");
  };

  return (
    <div className='customerSignup'>
      <Container>
        <Row>
          <Col sm='12'>
            <h1 className='text-center mb-3 mt-3'>Signup</h1>
            <Button
              variant='secondary'
              size='lg'
              className='w-100'
              disabled={viewMode !== "create"}
            >
              1/3 Create Account
            </Button>
            {viewMode === "create" && (
              <CustomerCreateAccount
                businessID={businessID}
                dismissMessages={dismissMessages}
                setErrorMessage={setErrorMessage}
                setSuccessMessage={setSuccessMessage}
                errorMessage={errorMessage}
                successMessage={successMessage}
                setSubmitClicked={setSubmitClicked}
                submitClicked={submitClicked}
                setViewMode={setViewMode}
                updateLoginKey={props.updateLoginKey}
                updateKey={updateKey}
                setUpdateKey={setUpdateKey}
              />
            )}
            <Button
              variant='secondary'
              size='lg'
              className='w-100'
              disabled={viewMode !== "details"}
            >
              2/3 Your Details
            </Button>
            {viewMode === "details" && (
              <AddYourInfo
                businessID={businessID}
                dismissMessages={dismissMessages}
                setErrorMessage={setErrorMessage}
                setSuccessMessage={setSuccessMessage}
                errorMessage={errorMessage}
                successMessage={successMessage}
                setSubmitClicked={setSubmitClicked}
                submitClicked={submitClicked}
                setViewMode={setViewMode}
                updateKey={updateKey}
                setUpdateKey={setUpdateKey}
              />
            )}
            <Button
              variant='secondary'
              size='lg'
              className='w-100'
              disabled={viewMode !== "participant"}
            >
              3/3 First Participant
            </Button>
            {viewMode === "addParticipant" && (
              <AddParticipant
                businessID={businessID}
                dismissMessages={dismissMessages}
                setErrorMessage={setErrorMessage}
                setSuccessMessage={setSuccessMessage}
                errorMessage={errorMessage}
                successMessage={successMessage}
                setSubmitClicked={setSubmitClicked}
                submitClicked={submitClicked}
                setViewMode={setViewMode}
                updateKey={updateKey}
                setUpdateKey={setUpdateKey}
                customer={customer}
                isDashboard={false}
              />
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const CustomerCreateAccount = (props) => {
  // Signup States
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  // Modal States
  const [termsAndConditionsIsOpen, setTermsAndConditionsIsOpen] = useState(false);
  const [privacyPolicyIsOpen, setPrivacyPolicyIsOpen] = useState(false);
  // Handle Password Strength
  const passwordRegexStrong = new RegExp(
    "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})"
  );
  const passwordRegexStrong2 = new RegExp(
    "^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{20,})"
  );
  const passwordRegexMedium = new RegExp(
    "^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})"
  );
  // Password Booleans & Tests
  const checkPasswordMedium = passwordRegexMedium.test(password);
  const checkPasswordStrong =
    passwordRegexStrong.test(password) || passwordRegexStrong2.test(password);
  const passwordTyped = password.length > 0;
  const passwordMatched = password === passwordConfirm;

  const currentKey = props.updateKey;

  const createAccount = async (e) => {
    e.preventDefault();
    props.setSubmitClicked(true);
    // Reject if password too weak somehow past the checks
    if (!checkPasswordMedium) {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "This is a weak password. Please make your password AT LEAST six characters and include an uppercase character and a number."
      );
    }
    // Reject if password somehow doesn't match.
    if (password !== passwordConfirm) {
      props.setSubmitClicked(false);
      return props.setErrorMessage("Your password fields don't match.");
    }
    // If terms and conditions aren't agreed, reject.
    if (
      !document.getElementById("privacy_check").checked ||
      !document.getElementById("term_check").checked
    ) {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "You must agree to our terms and conditions and privacy policy to use this service."
      );
    }

    // Construct the Data Packet
    const data = {
      email,
      password,
      businessID: props.businessID,
    };
    // Do the submit request
    const createAccountSubmit = await axios.post(
      `/api/customer/customer_users/CustomerSignup`,
      data,
      {
        withCredentials: true,
      }
    );

    if (createAccountSubmit.status === 200) {
      TagManager.dataLayer({
        dataLayer: {
          event: "registered",
        },
      });
      await login(data);
      props.updateLoginKey();
      props.setSubmitClicked(false);
      return props.setUpdateKey(currentKey + 1);
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage(
        "There was an error - perhaps you already have an account with us? Try logging in."
      );
    }
  };

  return (
    <FadeIn delay={250} duration={450}>
      <Form className='shadow p-4 mx-auto' onSubmit={createAccount}>
        <Form.Group controlId='LessonTitleForm' className='mt-3'>
          <Form.Label>Email Address</Form.Label>
          <Form.Control
            value={email}
            onChange={(e) => setEmail(e.target.value.toLowerCase())}
            required
            type='email'
          />
          <Form.Text className='text-muted'>
            We need your email so that you can login later, and so that we can communicate with you
            about your services.
          </Form.Text>
        </Form.Group>
        <Form.Group controlId='Password'>
          <Form.Label>Password</Form.Label>
          <Form.Control
            type='password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Form.Text className='text-muted'>
            Please set a secure password. Please keep your password secret and in a safe place.
          </Form.Text>
        </Form.Group>
        <Form.Group controlId='PasswordConfirm'>
          <Form.Label>Confirm Password</Form.Label>
          <Form.Control
            type='password'
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            required
          />
          <Form.Text className='text-muted'>
            Please type your password a second time so we know you meant it :)
          </Form.Text>
        </Form.Group>
        {!passwordMatched && <PasswordMismatchSpan />}
        {passwordMatched && passwordTyped && !checkPasswordMedium && !checkPasswordStrong && (
          <PasswordWeakSpan />
        )}
        {passwordMatched && checkPasswordMedium && !checkPasswordStrong && <PasswordMediumSpan />}
        {passwordMatched && checkPasswordStrong && <PasswordStrongSpan />}
        <Form.Group controlId='TandC' className='text-center'>
          <Form.Check type='checkbox' id='term_check' />
          <Form.Label>
            I agree to your{" "}
            <span onClick={(e) => setTermsAndConditionsIsOpen(true, e)}>
              <strong>
                <mark>terms and conditions</mark>
              </strong>
            </span>
            .
          </Form.Label>
        </Form.Group>
        <Form.Group controlId='Privacy' className='text-center'>
          <Form.Check type='checkbox' id='privacy_check' />
          <Form.Label>
            I agree to your{" "}
            <span onClick={(e) => setPrivacyPolicyIsOpen(true, e)}>
              <strong>
                <mark>privacy policy</mark>
              </strong>
            </span>
            .
          </Form.Label>
        </Form.Group>
        {props.submitClicked || props.errorMessage !== "" ? (
          <Button variant='info' className='w-100 mt-2 mb-2 text-center' type='button' disabled>
            Processing...
          </Button>
        ) : (
          <Button
            variant='primary'
            className='w-100 mt-2 mb-2 text-center'
            type='submit'
            disabled={!passwordMatched || !passwordTyped || !checkPasswordMedium}
          >
            Create Account <MdAccountBox />
          </Button>
        )}
        <div className='mt-2 mb-2'>
          <StatusBlock
            errorMessage={props.errorMessage}
            successMessage={props.successMessage}
            dismissMessages={props.dismissMessages}
          />
        </div>
      </Form>
      {/*T&C MODAL*/}
      <Modal
        show={termsAndConditionsIsOpen}
        size='lg'
        onHide={(e) => setTermsAndConditionsIsOpen(false, e)}
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Terms & Conditions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TermsAndConditions />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={(e) => setTermsAndConditionsIsOpen(false, e)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/*PRIVACY POLICY MODAL*/}
      <Modal
        show={privacyPolicyIsOpen}
        size='lg'
        onHide={(e) => setPrivacyPolicyIsOpen(false, e)}
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Privacy Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PrivacyPolicy />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={(e) => setPrivacyPolicyIsOpen(false, e)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </FadeIn>
  );
};

const AddYourInfo = (props) => {
  const [First_Name, setFirst_Name] = useState("");
  const [Last_Name, setLast_Name] = useState("");
  const [Postcode, setPostcode] = useState("");
  const [Street_Address, setStreet_Address] = useState("");
  const [City, setCity] = useState("");
  const [Contact_Number, setContact_Number] = useState("");
  const [heardAbout, setHeardAbout] = useState("Can't remember or don't want to say");
  const [marketingConsent, setMarketingConsent] = useState("No");

  const currentKey = props.updateKey;

  const capitalize = (string) => {
    return string.charAt(0, 1).toUpperCase() + string.slice(1, string.length);
  };

  const createCustomer = async (e) => {
    e.preventDefault();
    props.setSubmitClicked(true);
    const data = {
      First_Name: First_Name.trim(),
      Last_Name: Last_Name.trim(),
      Contact_Number: Contact_Number.trim(),
      Street_Address: Street_Address.trim(),
      City: City.trim(),
      Postcode: Postcode.trim(),
      heardAbout: heardAbout,
      marketingConsent: marketingConsent,
    };

    const createCustomerRequest = await axios.post(
      `/api/customer/manage_customer/create_customer`,
      data,
      {
        withCredentials: true,
      }
    );
    if (createCustomerRequest.status === 200) {
      TagManager.dataLayer({
        dataLayer: {
          event: "addYourInfoStage",
        },
      });
      props.setSubmitClicked(false);
      return props.setUpdateKey(currentKey + 1);
    } else {
      props.setSubmitClicked(false);
      return props.setErrorMessage("There was an error - please contact support if it");
    }
  };

  return (
    <FadeIn delay={250} duration={450}>
      <Form className='shadow p-4 mx-auto' onSubmit={createCustomer}>
        <Row>
          <Col sm='12' lg='4'>
            <Form.Group controlId='FirstNamesForm'>
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type='text'
                maxLength='256'
                value={First_Name}
                onChange={(e) => setFirst_Name(capitalize(e.target.value))}
                required
                className='border'
              />
            </Form.Group>
          </Col>
          <Col sm='12' lg='4'>
            <Form.Group controlId='LastNamesForm'>
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type='text'
                maxLength='256'
                value={Last_Name}
                onChange={(e) => setLast_Name(capitalize(e.target.value))}
                required
                className='border'
              />
            </Form.Group>
          </Col>
          <Col sm='12' lg='4'>
            <Form.Group controlId='Contact No'>
              <Form.Label>Contact Number</Form.Label>
              <Form.Control
                type='tel'
                pattern='^[0-9 ]{1,15}$'
                value={Contact_Number}
                onChange={(e) => setContact_Number(e.target.value)}
                required
                className='border'
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm='12' lg='12'>
            <Form.Group controlId='Adress1Form'>
              <Form.Label>Street Address</Form.Label>
              <Form.Control
                type='text'
                maxLength='256'
                value={Street_Address}
                onChange={(e) => setStreet_Address(e.target.value)}
                required
                className='border'
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm='12' lg='6'>
            <Form.Group controlId='Adress2Form'>
              <Form.Label>City</Form.Label>
              <Form.Control
                type='text'
                maxLength='256'
                value={City}
                onChange={(e) => setCity(e.target.value)}
                required
                className='border'
              />
            </Form.Group>
          </Col>

          <Col sm='12' lg='6'>
            <Form.Group controlId='EndTimeForm'>
              <Form.Label>Postcode/ZIP</Form.Label>
              <Form.Control
                type='text'
                maxLength='256'
                pattern='^[0-9 a-zA-Z]{1,10}$'
                value={Postcode}
                onChange={(e) => setPostcode(e.target.value)}
                required
                className='border'
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm='12' lg='6'>
            <Form.Group controlId='EndTimeForm'>
              <Form.Label>How did you hear about us?</Form.Label>
              <Form.Control
                as='select'
                value={heardAbout}
                onChange={(e) => setHeardAbout(e.target.value)}
                custom
                className='border'
              >
                <option value="Can't remember or don't want to say">
                  Can&apos;t remember or don&apos;t want to say
                </option>
                <option value='Someone I know'>Someone I know</option>
                <option value='Search Engine'>Google / Bing / Other search engine</option>
                <option value='Facebook / Social Media'>Facebook / Social Media</option>
                <option value='Leaflet'>Leaflet</option>
                <option value='Enquiry at other premises'>Enquiry at sport&apos;s premises</option>
                <option value='Another Organisation'>Another organisation</option>
              </Form.Control>
            </Form.Group>
          </Col>

          <Col sm='12' lg='6'>
            <Form.Group controlId='MarketingConsent'>
              <Form.Label>May we send you non-essential marketing emails?</Form.Label>
              <Form.Control
                as='select'
                value={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.value)}
                custom
                className='border'
              >
                <option value='No'>No, Thank You.</option>
                <option value='Yes'>Yes please.</option>
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col sm='12' className='text-center mt-4 mb-2'>
            {props.submitClicked || props.errorMessage !== "" ? (
              <Button
                variant='success'
                className='w-100 mt-2 mb-2 text-center '
                type='button'
                disabled
              >
                Processing...
              </Button>
            ) : (
              <Button variant='primary' className='w-100 mt-2 mb-2 text-center ' type='submit'>
                Save <MdSave />
              </Button>
            )}
            <div className='mt-2 mb-2'>
              <StatusBlock
                errorMessage={props.errorMessage}
                successMessage={props.successMessage}
                dismissMessages={props.dismissMessages}
              />
            </div>
          </Col>
        </Row>
      </Form>
    </FadeIn>
  );
};

//export the component
export default CustomerSignupForm;
